import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FilterProfileService } from '@services/filter-profile.service';
import { LogService } from '@services/log.service';
import {
  BehaviorSubject,
  Observable,
  Subject,
  takeUntil,
  take,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { IFilterProfile } from '@interfaces/tankProfile';
import { FormBuilder, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { SettingsDialogService } from '@components/settings/services/settings-dialog.service';
import { TapAppStore } from '@state/tap-state';
import { AppState } from '../../app.state';
import { tap } from 'rxjs/operators';
import { AppDialogService } from '@services/app-dialog.service';
import { ValidationState } from '@utils/validators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'filter-profile-settings',
  templateUrl: './filter-profile-settings.component.html',
  styleUrls: ['./filter-profile-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterProfileSettingsComponent implements OnDestroy {
  isMobileView: boolean;
  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    const target = event.target as Window;
    this.checkMobileView();
  }

  filterProfile: IFilterProfile;
  filterModel: any;
  filterForm: FormGroup = new UntypedFormGroup({});
  isLoading$ = new BehaviorSubject<boolean>(true);
  
  isLoading = this.isLoading$.asObservable();
  
  destroy$ = new Subject<boolean>();

  // Local validation state
  validationState: ValidationState = {
    filterProfileName: { isValid: true, isChecking: false },
    isPrivate: { isValid: true, isChecking: false },
  };

  // Original values for comparison
  private originalValues = {
    filterProfileName: '',
    isPrivate: 0,
  };

  get isReadOnly(): boolean {
    return this.filterProfile?.isReadOnly ?? false;
  }

  lastSelectedProfileId: number;
  lastSelectedProfile$: Observable<IFilterProfile> = this.tapStore.getCurrentFilterProfile$().pipe(
    tap(fp => (this.lastSelectedProfileId = fp.filterProfileId)),
    takeUntil(this.destroy$)
  );

  private _currentFilterProfile: IFilterProfile;

  @Output()
  onFilterProfileSaved = new EventEmitter<IFilterProfile>();

  @Input()
  public set currentFilterProfile(value: IFilterProfile) {
    this._currentFilterProfile = value;
    this.loadFilterProfile(value);
  }
  public get currentFilterProfile(): IFilterProfile {
    return this._currentFilterProfile;
  }

  public get filters() {
    return Object.keys(this.filterModel || {});
  }

  public get disableSave(): boolean {
    return (
      this.filterForm?.disabled ||
      this.filterForm?.pristine ||
      this.hasValidationErrors ||
      this.isAnyValidationChecking
    );
  }

  public get hasValidationErrors(): boolean {
    return (
      this.filterForm.invalid ||
      !this.validationState.filterProfileName.isValid ||
      !this.validationState.isPrivate.isValid
    );
  }

  public get isAnyValidationChecking(): boolean {
    return (
      this.validationState.filterProfileName.isChecking || this.validationState.isPrivate.isChecking
    );
  }

  public get isDirty(): boolean {
    return this.filterForm.dirty || this.isAnyValidationChecking;
  }

  constructor(
    private readonly filterProfileService: FilterProfileService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly logger: LogService,
    private readonly fb: FormBuilder,
    private readonly settingsDialogService: SettingsDialogService,
    private readonly tapStore: TapAppStore,
    private readonly ads: AppDialogService,
    private readonly translate: TranslateService
  ) {}

  public resetValidationState() {
    this.validationState = {
      filterProfileName: { isValid: true, isChecking: false },
      isPrivate: { isValid: true, isChecking: false },
    };
    this.changeDetectorRef.markForCheck();
  }



  save() {
    if (this.hasValidationErrors || this.isReadOnly || this.isAnyValidationChecking) {
      return;
    }

    if (!this.filterForm.valid) {
      return;
    }

    const formValue = this.filterForm.value;
    const updatedProfile = {
      ...this.filterProfile,
      filterProfileName: this.filterForm.get('filterProfileName').getRawValue(),
      isPrivate: formValue.isPrivate,
      tankFilterJson: JSON.stringify(this.filterModel),
    };
    this.saveFilterProfile(updatedProfile);

  }

  reset() {
    if (this.filterProfile && !this.isReadOnly) {
      this.loadFilterProfile(this.filterProfile);
      this.logger.info('Filter profile form reset to original state');
      this.ads.showGeneralMessage(this.translate.instant('FilterProfile.ResetSuccess'));
    }
  }

  cancel() {
    this.settingsDialogService.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private saveFilterProfile(filterProfile: IFilterProfile) {
    this.filterProfileService.saveFilterProfile(filterProfile).subscribe(
      (response: IFilterProfile) => {
        this.logger.info('Filter profile updated', response);
        this.ads.showSuccessMessage('Filter profile updated successfully');
        this.onFilterProfileSaved.emit(response);
      },
      error => {
        this.logger.error('Error updating filter profile', error);
      }
    );
  }

    private performDependencyValidation() {
        this.filterProfileService
      .filterProfileHasDependents(this.filterProfile?.filterProfileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: hasDependents => {
          this.validationState.isPrivate = {
            isValid: !hasDependents,
            errorMessage: hasDependents
              ? 'FilterProfile.Form.FilterProfileHasDependents'
              : undefined,
            isChecking: false,
          };
          this.changeDetectorRef.markForCheck();
          if (hasDependents) {
            this.filterProfileService.showFilterProfileHasDependentsError();
          }
        },
        error: error => {
          this.logger.error('Error checking filter profile dependents', error);
          this.validationState.isPrivate = { isValid: true, isChecking: false };
          this.changeDetectorRef.markForCheck();
        },
      });

  }

  private loadFilterProfile(profile: IFilterProfile) {
    this.isLoading$.next(true);
    
    setTimeout(() => {

    this.changeDetectorRef.detectChanges();

    this.filterProfile = profile;
    this.filterModel = profile?.tankFilterJson ? JSON.parse(profile.tankFilterJson) : null;

    // Store original values for comparison
    this.originalValues = {
      filterProfileName: profile?.filterProfileName || '',
      isPrivate: profile?.isPrivate ? 1 : 0,
    };

    // Reset validation state
    this.resetValidationState();

    // Initialize form on first load, patch on subsequent loads
    if (!this.filterForm || !this.filterForm.controls.filterProfileName) {
      this.createForm(profile);
      this.setupValidationListeners();
    } else {
      this.patchFormValues(profile);
    }

    // Handle read-only state
    if (this.isReadOnly) {
      this.filterForm.disable();
    } else {
      this.filterForm.enable();
      // Keep filterProfileName disabled as per your original logic
      this.filterForm.get('filterProfileName')?.disable();
    }
    this.checkMobileView();
    this.isLoading$.next(false);
    this.changeDetectorRef.markForCheck();
    this.logger.info('Filter profile loaded', this.filterProfile, this.filterModel);
  },  0);
  
  }

  private createForm(profile: IFilterProfile) {
    this.filterForm = this.fb.group({
      isPrivate: [{ value: profile?.isPrivate ? 1 : 0, disabled: this.isReadOnly }],
      filterProfileName: [
        { value: profile?.filterProfileName, disabled: true },
        [Validators.required, Validators.maxLength(80)],
      ],
    });
  }

  private patchFormValues(profile: IFilterProfile) {
    // Reset form state
    this.filterForm.markAsUntouched();
    this.filterForm.markAsPristine();

    // Patch the values
    this.filterForm.patchValue({
      isPrivate: profile?.isPrivate ? 1 : 0,
      filterProfileName: profile?.filterProfileName,
    });

    // Clear any previous validation errors
    Object.keys(this.filterForm.controls).forEach(key => {
      this.filterForm.get(key)?.setErrors(null);
    });
  }

  private setupValidationListeners() {
    // Listen to isPrivate changes
    this.filterForm
      .get('isPrivate')
      .valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        this.validateIsPrivate(value);
      });

    // Listen to filterProfileName changes
    this.filterForm
      .get('filterProfileName')
      .valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        this.validateFilterProfileName(value);
      });
  }

  private validateIsPrivate(value: number) {
    // Skip validation if value hasn't changed or is public
    if (value === this.originalValues.isPrivate || value === 0) {
      this.validationState.isPrivate = { isValid: true, isChecking: false };
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.validationState.isPrivate = { isValid: true, isChecking: true };
    this.changeDetectorRef.markForCheck();

    this.performDependencyValidation();
  }
  
  private validateFilterProfileName(value: string) {
    // Skip validation if value hasn't changed or is empty
    if (value === this.originalValues.filterProfileName || !value) {
      this.validationState.filterProfileName = { isValid: true, isChecking: false };
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.validationState.filterProfileName = { isValid: true, isChecking: true };
    this.changeDetectorRef.markForCheck();

    this.filterProfileService
      .filterProfileNameAlreadyExists(value, this.filterProfile?.filterProfileId, this.filterProfile?.organizationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: exists => {
          this.validationState.filterProfileName = {
            isValid: !exists,
            errorMessage: exists ? 'FilterProfile.Form.FilterProfileNameAlreadyExists' : undefined,
            isChecking: false,
          };
          this.changeDetectorRef.markForCheck();
        },
        error: error => {
          this.logger.error('Error checking filter profile name', error);
          this.validationState.filterProfileName = { isValid: true, isChecking: false };
          this.changeDetectorRef.markForCheck();
        },
      });
  }
  private checkMobileView() {
    setTimeout(() => {
    const formField = document.querySelectorAll('.profile-row.form-fields')[0];

    if(!formField)
      return;

      //get parent element width
      const parentElement = formField.parentElement;
      const parentWidth = Number.parseFloat(getComputedStyle(parentElement).width);
      const formFieldWidth = Number.parseFloat(getComputedStyle(formField).width);
      const allowedPadding = 21;
      if (formFieldWidth > (parentWidth - allowedPadding)) {
        this.isMobileView = true;
        parentElement.classList.add('flex-wrapped')
        this.changeDetectorRef.markForCheck();
      }
      else {
        this.isMobileView = false;
        parentElement.classList.remove('flex-wrapped');
        this.changeDetectorRef.markForCheck();
      }
    }, 100);
  }

}
